import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, BookOpen, Save, Trash2, Plus, MessageSquare, Loader2 } from 'lucide-react';

export default function NotebookPanel({ apiUrl, isOpen, onClose, preloadDocId }) {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [attachedDoc, setAttachedDoc] = useState(null);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveContent, setSaveContent] = useState('');
  const messagesEndRef = useRef(null);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/notebook/sessions`);
      const data = await res.json();
      setSessions(data);
    } catch (e) { console.error(e); }
  }, [apiUrl]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/documents?page_size=200`);
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (e) { console.error(e); }
  }, [apiUrl]);

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
      fetchDocuments();
      if (preloadDocId) setAttachedDoc(preloadDocId);
    }
  }, [isOpen, preloadDocId, fetchSessions, fetchDocuments]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSession = async (sessionId) => {
    try {
      const res = await fetch(`${apiUrl}/notebook/sessions/${sessionId}`);
      const data = await res.json();
      setActiveSession(sessionId);
      setMessages(data.messages || []);
    } catch (e) { console.error(e); }
  };

  const startNewSession = () => {
    setActiveSession(null);
    setMessages([]);
    setAttachedDoc(null);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const body = { message: userMsg, session_id: activeSession || undefined };
      if (attachedDoc) body.document_id = attachedDoc;

      const res = await fetch(`${apiUrl}/notebook/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!activeSession) setActiveSession(data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        document_title: data.document_title,
        timestamp: new Date().toISOString(),
      }]);
      setAttachedDoc(null);
      fetchSessions();
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + e.message, timestamp: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await fetch(`${apiUrl}/notebook/sessions/${sessionId}`, { method: 'DELETE' });
      if (activeSession === sessionId) startNewSession();
      fetchSessions();
    } catch (e) { console.error(e); }
  };

  const saveInsight = async () => {
    if (!saveTitle.trim() || !saveContent.trim()) return;
    try {
      const res = await fetch(`${apiUrl}/notebook/save-insight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: saveTitle, content: saveContent, session_id: activeSession || 'manual' }),
      });
      const data = await res.json();
      setShowSaveDialog(false);
      setSaveTitle('');
      setSaveContent('');
      alert(data.status === 'saved' ? `Saved "${data.title}" to library!` : `Already exists: ${data.title}`);
    } catch (e) { console.error(e); }
  };

  const openSaveDialog = (content) => {
    setSaveContent(content);
    setSaveTitle('');
    setShowSaveDialog(true);
  };

  if (!isOpen) return null;

  const attachedDocData = documents.find(d => d.id === attachedDoc);

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[50vw] bg-slate-950 border-l border-cyan-900/30 z-50 flex flex-col shadow-2xl shadow-cyan-900/20" data-testid="notebook-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-cyan-400" />
          <span className="text-sm font-semibold text-cyan-400 tracking-wide">AI NOTEBOOK</span>
          <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded">GEMINI</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={startNewSession} className="text-slate-400 hover:text-cyan-400 p-1" title="New session" data-testid="notebook-new-session">
            <Plus className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="notebook-close">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Session sidebar */}
        <div className="w-48 border-r border-slate-800/50 overflow-y-auto bg-slate-950/50 hidden sm:block">
          <div className="p-2 text-[10px] uppercase text-slate-500 tracking-widest">Sessions</div>
          {sessions.map(s => (
            <div key={s.session_id}
              className={`group px-2 py-2 cursor-pointer text-xs border-b border-slate-800/30 hover:bg-slate-900 flex items-center justify-between ${activeSession === s.session_id ? 'bg-slate-900 text-cyan-400' : 'text-slate-400'}`}
              onClick={() => loadSession(s.session_id)}
            >
              <div className="truncate flex-1">{s.preview || 'New chat'}</div>
              <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 p-0.5" title="Delete">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          {sessions.length === 0 && <div className="p-3 text-[11px] text-slate-600">No sessions yet</div>}
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-slate-500 mt-20">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Ask anything about the library&apos;s documents.</p>
                <p className="text-xs mt-1 text-slate-600">Attach a document with the book icon below.</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={msg.id || `msg-${msg.role}-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-cyan-900/30 text-cyan-100 border border-cyan-800/30'
                    : 'bg-slate-800/60 text-slate-200 border border-slate-700/30'
                }`}>
                  {msg.document_title && (
                    <div className="text-[10px] text-cyan-500 mb-1 flex items-center gap-1">
                      <BookOpen className="w-3 h-3" /> {msg.document_title}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                  {msg.role === 'assistant' && (
                    <button onClick={() => openSaveDialog(msg.content)}
                      className="mt-1 text-[10px] text-slate-500 hover:text-cyan-400 flex items-center gap-1">
                      <Save className="w-3 h-3" /> Save to library
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800/60 rounded-lg px-3 py-2 text-sm text-slate-400 flex items-center gap-2 border border-slate-700/30">
                  <Loader2 className="w-4 h-4 animate-spin" /> Thinking...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Attached doc indicator */}
          {attachedDocData && (
            <div className="mx-4 mb-2 flex items-center gap-2 text-xs text-cyan-400 bg-cyan-900/20 px-2 py-1 rounded border border-cyan-800/30">
              <BookOpen className="w-3 h-3" />
              <span className="truncate">{attachedDocData.title}</span>
              <button onClick={() => setAttachedDoc(null)} className="ml-auto text-slate-500 hover:text-red-400"><X className="w-3 h-3" /></button>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-slate-800 bg-slate-950/80">
            <div className="flex items-center gap-2">
              <button onClick={() => setShowDocPicker(!showDocPicker)}
                className={`p-2 rounded ${attachedDoc ? 'text-cyan-400 bg-cyan-900/30' : 'text-slate-500 hover:text-cyan-400'}`}
                title="Attach document" data-testid="notebook-attach-doc">
                <BookOpen className="w-4 h-4" />
              </button>
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Ask about your documents..."
                className="flex-1 bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-700"
                data-testid="notebook-input"
              />
              <button onClick={sendMessage} disabled={loading || !input.trim()}
                className="p-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 rounded-md text-white"
                data-testid="notebook-send">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document picker modal */}
      {showDocPicker && (
        <div className="absolute bottom-16 left-4 right-4 sm:left-52 max-h-64 bg-slate-900 border border-slate-700 rounded-lg overflow-y-auto shadow-xl z-10">
          <div className="p-2 text-[10px] uppercase text-slate-500 tracking-widest border-b border-slate-800">Select a document</div>
          {documents.map(d => (
            <div key={d.id}
              onClick={() => { setAttachedDoc(d.id); setShowDocPicker(false); }}
              className="px-3 py-2 text-xs text-slate-300 hover:bg-slate-800 cursor-pointer border-b border-slate-800/30 flex items-center gap-2">
              <BookOpen className="w-3 h-3 text-cyan-600 shrink-0" />
              <span className="truncate">{d.title}</span>
              <span className="text-[10px] text-slate-600 ml-auto shrink-0">{d.category}</span>
            </div>
          ))}
        </div>
      )}

      {/* Save insight dialog */}
      {showSaveDialog && (
        <div className="absolute inset-0 bg-black/60 z-20 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 w-full max-w-md">
            <h3 className="text-sm font-semibold text-cyan-400 mb-3">Save Insight to Library</h3>
            <input value={saveTitle} onChange={e => setSaveTitle(e.target.value)} placeholder="Title for this insight"
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white mb-3 focus:outline-none focus:border-cyan-700" />
            <textarea value={saveContent} onChange={e => setSaveContent(e.target.value)} rows={6}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white mb-3 focus:outline-none focus:border-cyan-700 resize-none" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSaveDialog(false)} className="px-3 py-1.5 text-xs text-slate-400 hover:text-white">Cancel</button>
              <button onClick={saveInsight} className="px-3 py-1.5 text-xs bg-cyan-600 hover:bg-cyan-500 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
